import { useEffect, useMemo, useState } from "react";
import { OrderTypeTag } from "../components/OrderTypeTag";
import api, { formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Navigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { PageHeader } from "../components/GradientCard";
import { moduleCanDo } from "../lib/modules";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { Plus, Receipt, Search, Check, X, Eye } from "lucide-react";
import { useGlobalViewMode } from "../components/ViewModeToggle";
import DeleteWithReasonDialog from "../components/DeleteWithReasonDialog";
import { OrderForm, emptyForm as emptyOrderForm, OrderDetailDialog } from "./OrderWorkflow";

const PAY_COLOR = {
  "Pending": "bg-amber-100 text-amber-800",
  "Paid": "bg-green-100 text-green-700",
  "Overdue": "bg-red-100 text-red-700",
  "Cancelled": "bg-slate-200 text-slate-600 line-through",
};

export default function InvoiceWorkflow({ embedded = false } = {}) {
  const { user } = useAuth();
  const [viewMode] = useGlobalViewMode();
  const canAccess = user?.role === "master_admin" || moduleCanDo(user, "sales_orders", "edit");
  const isMaster = user?.role === "master_admin" || moduleCanDo(user, "sales_orders", "edit");

  const [orders, setOrders] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [tab, setTab] = useState("pending");
  const [invFilter, setInvFilter] = useState(""); // "" | "Paid" | "Pending" | "Overdue" | "Cancelled"
  const [creating, setCreating] = useState(null); // order being invoiced
  const [editing, setEditing] = useState(null); // invoice being edited (payment toggle)
  const [form, setForm] = useState(emptyForm());
  // Editable Order Form state — used inside the Generate Invoice dialog so the
  // user can adjust order details before raising the first invoice.
  const [orderForm, setOrderForm] = useState(emptyOrderForm());
  const [orderOriginal, setOrderOriginal] = useState(null);
  const [orderEditRemark, setOrderEditRemark] = useState("");
  // Whether the user has explicitly clicked "Edit" on the order summary inside
  // the Generate Invoice dialog. Defaults to false so the dialog opens with a
  // read-only summary as requested.
  const [orderEditMode, setOrderEditMode] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [search, setSearch] = useState("");
  const [docReqs, setDocReqs] = useState([]);
  const [serviceCalcs, setServiceCalcs] = useState([]);
  const [selectedCalcs, setSelectedCalcs] = useState(() => new Set());
  // Credit Note dialog state — Master/Accounts Admin issues against a
  // Complete-Delivered invoice.
  const [cnTarget, setCnTarget] = useState(null);
  const [cnForm, setCnForm] = useState({ credit_note_no: "", amount: "", reason: "", remark: "", credit_note_date: new Date().toISOString().slice(0, 10) });
  // Credit Notes — listed in the new "Credit Notes" tab.
  const [creditNotes, setCreditNotes] = useState([]);
  const [cnDetail, setCnDetail] = useState(null);
  const [cnEditing, setCnEditing] = useState(null);
  const [pendingDeleteInvoice, setPendingDeleteInvoice] = useState(null);
  const [pendingDeleteCN, setPendingDeleteCN] = useState(null);
  const [viewingOrder, setViewingOrder] = useState(null);
  const viewOrderDetails = async (orderId) => {
    try {
      const { data } = await api.get(`/orders/${orderId}/full`);
      setViewingOrder(data);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const loadCreditNotes = async () => {
    try {
      const { data } = await api.get("/credit-notes");
      setCreditNotes(data || []);
    } catch { /* silent */ }
  };
  useEffect(() => { loadCreditNotes(); }, []);

  const loadServiceCalcs = async () => {
    try {
      const { data } = await api.get("/invoices/pending-service-calculations");
      setServiceCalcs(data || []);
    } catch (e) { /* silent if no module */ }
  };

  const loadDocReqs = async () => {
    try {
      const { data } = await api.get("/dispatch-doc-requests");
      setDocReqs(data || []);
    } catch (e) { /* silent */ }
  };

  const [docFulfillForm, setDocFulfillForm] = useState(null); // { req, url, eway_url, eway_no }
  const openFulfillDocReq = (r) => setDocFulfillForm({ req: r, url: "", eway_url: "", eway_no: "" });
  const submitDocFulfill = async () => {
    if (!docFulfillForm) return;
    const { req: r, url, eway_url, eway_no } = docFulfillForm;
    if (!url.trim()) { toast.error(`${r.request_type === "bill_copy" ? "Bill Copy" : "Quote"} URL is required`); return; }
    try {
      await api.post(`/dispatch-doc-requests/${r.id}/fulfill`, {
        url: url.trim(),
        eway_url: eway_url.trim(),
        eway_no: eway_no.trim(),
      });
      toast.success("URL shared with Dispatch ✓");
      setDocFulfillForm(null);
      loadDocReqs();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const fulfillDocReq = (r) => openFulfillDocReq(r);

  const load = async () => {
    try {
      const [oRes, iRes] = await Promise.all([api.get("/orders?scope=all"), api.get("/invoices")]);
      setOrders(oRes.data);
      setInvoices(iRes.data);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  useEffect(() => { if (canAccess) { load(); loadDocReqs(); loadServiceCalcs(); } }, [canAccess]); // eslint-disable-line

  // Pending invoice list (PRODUCT orders only — Service orders flow via SOM "Submit for Invoicing")
  const invoicedOrderIds = useMemo(() => new Set(invoices.filter((i) => !i.is_cancelled).map((i) => i.order_id)), [invoices]);
  // Lookup for invoice rows to determine if their parent order is Complete
  // Delivered (locks edit/cancel/delete and unlocks the Credit Note action).
  const ordersById = useMemo(() => Object.fromEntries(orders.map((o) => [o.id, o])), [orders]);
  //  - Exclude cancelled orders
  //  - Exclude Service orders entirely
  //  - Include all Product orders that allow multiple invoices
  //  - For Product (default), include only if not yet invoiced with an active (non-cancelled) invoice
  const pendingOrders = useMemo(() => orders.filter((o) => {
    if (o.status === "Cancelled") return false;
    if ((o.order_type || "Product") === "Service") return false;
    if (o.allow_multiple_invoices) return true;
    return !invoicedOrderIds.has(o.id);
  }), [orders, invoicedOrderIds]);

  const invoiceCountByOrder = useMemo(() => {
    const m = {};
    invoices.forEach((i) => { m[i.order_id] = (m[i.order_id] || 0) + 1; });
    return m;
  }, [invoices]);

  const filteredInvoices = useMemo(() => {
    const q = search.toLowerCase();
    return invoices.filter((i) => {
      if (q && ![i.invoice_no, i.client_name, i.order_no].some((v) => String(v ?? "").toLowerCase().includes(q))) return false;
      if (invFilter) {
        if (invFilter === "Cancelled") return !!i.is_cancelled;
        // Non-Cancelled filters exclude cancelled rows
        if (i.is_cancelled) return false;
        return (i.payment_status || "").toLowerCase() === invFilter.toLowerCase();
      }
      return true;
    });
  }, [invoices, search, invFilter]);

  if (!canAccess) return <Navigate to="/" replace />;

  const openCreate = (order) => {
    setCreating(order);
    const totalV = order.total_value || "";
    setForm({ ...emptyForm(), order_id: order.id, amount: totalV, amount_before_gst: "", gst_percent: "18", gst_amount: "" });
    // Seed the editable Order Form with the order's current state. We merge
    // into emptyOrderForm() so any newly-added fields default cleanly.
    const seeded = { ...emptyOrderForm(), ...order };
    setOrderForm(seeded);
    // Snapshot for change-detection later — only PATCH the order if something
    // in this snapshot actually differs from what the user submits.
    setOrderOriginal(seeded);
    setOrderEditRemark("");
    setOrderEditMode(false);
  };

  // Open invoice form pre-filled from selected SOM calculations (Service Calcs tab).
  const openCreateFromCalcs = () => {
    const picked = serviceCalcs.filter((c) => selectedCalcs.has(c.id));
    if (picked.length === 0) { toast.error("Pick at least one calculation"); return; }
    const orderIds = new Set(picked.map((c) => c.order_id));
    if (orderIds.size > 1) {
      toast.error("All selected calculations must belong to the same Service Order");
      return;
    }
    const orderId = picked[0].order_id;
    const order = orders.find((o) => o.id === orderId);
    if (!order) { toast.error("Linked order not found"); return; }
    const total = picked.reduce((acc, c) => acc + Number(c.invoice_total || 0), 0);
    const months = picked.map((c) => c.month_key).sort().join(", ");
    const remark = `Service Order Invoice — ${picked[0].product_service || "Service"} · Month(s): ${months}`;
    setCreating(order);
    setForm({
      ...emptyForm(),
      order_id: order.id,
      amount: String(total),
      amount_before_gst: String(total),
      gst_percent: "0",
      gst_amount: "0",
      remark,
      source_calculation_ids: picked.map((c) => c.id),
    });
  };

  const toggleCalcSelect = (id, orderId) => {
    const next = new Set(selectedCalcs);
    if (next.has(id)) {
      next.delete(id);
    } else {
      // Auto-clear selections from other orders to enforce "one order per invoice" rule.
      const sameOrderIds = new Set(serviceCalcs.filter((c) => c.order_id === orderId).map((c) => c.id));
      [...next].forEach((cid) => { if (!sameOrderIds.has(cid)) next.delete(cid); });
      next.add(id);
    }
    setSelectedCalcs(next);
  };

  const downloadAnnexure = async (inv) => {
    try {
      const res = await api.get(`/invoices/${inv.id}/annexure-pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `Annexure_${inv.invoice_no}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || "Could not download annexure"); }
  };

  // Diff helper — compares editable order fields between original snapshot and
  // the current form state. Returns a list of field keys that have changed.
  const orderChangedFields = () => {
    if (!orderOriginal) return [];
    const KEYS = [
      "client_name", "client_id", "invoice_company_name", "order_type",
      "allow_multiple_invoices",
      "billing_address", "billing_pincode", "billing_district", "billing_state",
      "shipping_same_as_billing", "shipping_address", "shipping_pincode",
      "contact_name", "contact_number",
      "is_gem_contract", "gem_contract_no", "gem_contract_date", "gem_last_delivery_date",
      "sale_associate_name", "sale_associate_id",
      "commission_type", "commission_value", "commission",
      "products", "product_service", "total_value", "remark",
      "has_po", "po_number", "po_date", "po_url",
      "is_agent_based", "agent_name", "agent_commission_type", "agent_commission_value",
    ];
    return KEYS.filter((k) => JSON.stringify(orderOriginal[k] ?? "") !== JSON.stringify(orderForm[k] ?? ""));
  };
  // Whether the current user is allowed to edit the order (Master / Accounts /
  // creator) and whether the order is still editable (no invoice yet).
  const isCreator = !!(creating && user?.id && creating.created_by_id === user.id);
  const orderEditable = !!creating && canAccess && (isMaster || moduleCanDo(user, "sales_orders", "edit") || isCreator) && (invoiceCountByOrder[creating.id] || 0) === 0;

  const submit = async () => {
    if (!form.invoice_no.trim() || !form.invoice_date) {
      toast.error("Invoice number and date are required");
      return;
    }
    if (!(parseFloat(form.amount_before_gst) > 0)) {
      toast.error("Before GST Amount is required (used for incentive calculations)");
      return;
    }
    // If the user explicitly entered Edit mode AND changed at least one
    // field, push those changes to the order (with audit trail) before raising
    // the invoice. Read-only summaries never trigger a PATCH.
    const changed = (orderEditable && orderEditMode) ? orderChangedFields() : [];
    if (changed.length > 0 && !orderEditRemark.trim()) {
      toast.error("Please add a remark describing the order changes (audit trail).");
      return;
    }
    setSubmitting(true);
    try {
      if (changed.length > 0) {
        // Mirror OrderWorkflow's payload massaging for numeric fields.
        const orderPayload = {
          ...orderForm,
          // Mirror billing → shipping when same-as-billing is on.
          shipping_address: orderForm.shipping_same_as_billing ? (orderForm.billing_address || "") : orderForm.shipping_address,
          shipping_pincode: orderForm.shipping_same_as_billing ? (orderForm.billing_pincode || "") : orderForm.shipping_pincode,
          shipping_district: orderForm.shipping_same_as_billing ? (orderForm.billing_district || "") : orderForm.shipping_district,
          shipping_state: orderForm.shipping_same_as_billing ? (orderForm.billing_state || "") : orderForm.shipping_state,
          agent_commission_value: parseFloat(orderForm.agent_commission_value) || 0,
          salesmen: (orderForm.salesmen || []).map((s) => ({
            user_id: s.user_id,
            percent: parseFloat(s.percent) || 0,
          })),
          edit_remark: orderEditRemark.trim() || "Edited during invoice generation",
          source: "invoice-generation",
        };
        await api.patch(`/orders/${creating.id}`, orderPayload);
      }
      await api.post("/invoices", form);
      toast.success(form.source_calculation_ids?.length ? "Invoice generated · SOM calc marked invoiced" : "Invoice generated");
      setCreating(null);
      setOrderOriginal(null);
      setOrderEditRemark("");
      setOrderEditMode(false);
      setSelectedCalcs(new Set());
      load();
      loadServiceCalcs();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  const togglePaid = async (inv) => {
    const remark = window.prompt(`Remark for marking invoice ${inv.invoice_no} as ${inv.is_paid ? "Unpaid" : "Paid"} (audit trail):`, inv.is_paid ? "Reversed payment status" : "Payment received");
    if (remark === null) return; // user cancelled
    if (!remark.trim()) { toast.error("Remark is required"); return; }
    try {
      await api.patch(`/invoices/${inv.id}`, { is_paid: !inv.is_paid, edit_remark: remark.trim() });
      toast.success(inv.is_paid ? "Marked unpaid" : "Marked paid");
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const deleteInvoice = (inv) => setPendingDeleteInvoice(inv);
  const confirmDeleteInvoice = async (reason) => {
    try {
      await api.delete(`/invoices/${pendingDeleteInvoice.id}`, { data: { reason } });
      toast.success("Invoice moved to Recycle Bin");
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); throw e; }
  };

  const cancelInvoice = async (inv) => {
    const reason = window.prompt(`Mark invoice ${inv.invoice_no} as Cancelled?\n\nEnter the reason (required):`);
    if (reason === null) return;
    if (!reason.trim()) { toast.error("A cancellation reason is required"); return; }
    try {
      await api.post(`/invoices/${inv.id}/cancel`, { reason: reason.trim() });
      toast.success(`Invoice ${inv.invoice_no} marked cancelled`);
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  // Credit Note — Master/Accounts Admin issues against a delivered invoice.
  // Backend rejects unless the parent order is `Complete Delivered`.
  const openCreditNote = (inv) => {
    setCnTarget(inv);
    setCnForm({ credit_note_no: "", amount: inv.amount || "", reason: "", remark: "", credit_note_date: new Date().toISOString().slice(0, 10) });
  };
  const submitCreditNote = async () => {
    if (!cnForm.reason.trim()) { toast.error("A reason is required"); return; }
    try {
      const { data } = await api.post(`/invoices/${cnTarget.id}/credit-note`, cnForm);
      toast.success(`Credit Note ${data.credit_note_no} issued`);
      setCnTarget(null);
      load();
      loadCreditNotes();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  // Credit Note edit + delete (Master / Accounts Admin)
  const openEditCN = (cn) => {
    setCnEditing({ ...cn });
  };
  const saveEditCN = async () => {
    if (!cnEditing) return;
    try {
      await api.patch(`/credit-notes/${cnEditing.id}`, {
        credit_note_no: cnEditing.credit_note_no,
        credit_note_date: cnEditing.credit_note_date,
        amount: cnEditing.amount,
        reason: cnEditing.reason,
        remark: cnEditing.remark,
      });
      toast.success("Credit Note updated");
      setCnEditing(null);
      load();
      loadCreditNotes();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const deleteCreditNote = (cn) => setPendingDeleteCN(cn);
  const confirmDeleteCN = async (reason) => {
    try {
      await api.delete(`/credit-notes/${pendingDeleteCN.id}`, { data: { reason } });
      toast.success(`Credit Note ${pendingDeleteCN.credit_note_no} moved to Recycle Bin`);
      load();
      loadCreditNotes();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); throw e; }
  };

  // Master Admin — rewind order from Complete Delivered → Pending Dispatch /
  // Pending Invoice. Surfaces in the All Invoices tab for delivered orders.
  const reopenOrder = async (orderId, toStatus, cascade) => {
    if (!window.confirm(`Rewind this order to "${toStatus}"?${cascade ? " Downstream invoices/dispatches/CNs may be removed." : ""}`)) return;
    try {
      const { data } = await api.post(`/orders/${orderId}/reopen`, { to_status: toStatus, cascade });
      toast.success(`Order rewound to ${toStatus}`);
      load();
      loadCreditNotes();
      console.log("Reopen result:", data);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const openEditInvoice = (inv) => {
    setEditing(inv);
    setForm({
      order_id: inv.order_id || "",
      invoice_no: inv.invoice_no || "",
      invoice_date: inv.invoice_date || "",
      gem_invoice_no: inv.gem_invoice_no || "",
      gem_invoice_url: inv.gem_invoice_url || "",
      amount: inv.amount || "",
      amount_before_gst: inv.amount_before_gst || "",
      gst_mode: inv.gst_mode || "percent",
      gst_percent: inv.gst_percent || "",
      gst_amount: inv.gst_amount || "",
      remark: inv.remark || "",
      is_paid: !!inv.is_paid,
      due_date: inv.due_date || "",
      eway_no: inv.eway_no || "",
      dispatch_via: inv.dispatch_via || "",
      edit_remark: "",
    });
  };

  const saveEditInvoice = async () => {
    if (!(form.edit_remark || "").trim()) {
      toast.error("Please add a remark describing what changed and why");
      return;
    }
    try {
      const body = { ...form };
      delete body.order_id;
      await api.patch(`/invoices/${editing.id}`, body);
      toast.success("Invoice updated");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const updateDueDate = async (inv, due) => {
    const remark = window.prompt("Remark for due-date change (audit trail):", "Updated due date");
    if (remark === null) return;
    if (!remark.trim()) { toast.error("Remark is required"); return; }
    try {
      await api.patch(`/invoices/${inv.id}`, { due_date: due, edit_remark: remark.trim() });
      toast.success("Due date updated");
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  return (
    <div className={embedded ? "space-y-6" : "max-w-[1500px] space-y-6"}>
      {!embedded && (
        <PageHeader
          moduleKey="sales_orders"
          tone="emerald"
          icon={Receipt}
          eyebrow="Accounts Workflow"
          title="Invoice Generation"
          subtitle="Generate invoices for confirmed orders and track payments."
        />
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className={embedded ? "bg-slate-100/70 dark:bg-slate-800/50 p-1 rounded-lg gap-1 h-auto flex-wrap" : ""}>
          <TabsTrigger value="pending" data-testid="inv-tab-pending">Pending Invoice ({pendingOrders.length})</TabsTrigger>
          <TabsTrigger value="service-calcs" data-testid="inv-tab-service-calcs">
            Service Calcs
            {serviceCalcs.length > 0 && (
              <span className="ml-1.5 inline-flex items-center justify-center bg-violet-600 text-white rounded-full text-[9px] font-bold w-4 h-4">{serviceCalcs.length}</span>
            )}
          </TabsTrigger>
          <TabsTrigger value="all" data-testid="inv-tab-all">All Invoices ({invoices.length})</TabsTrigger>
          <TabsTrigger value="credit-notes" data-testid="inv-tab-cn">Credit Notes ({creditNotes.length})</TabsTrigger>
          <TabsTrigger value="dispatch-reqs" data-testid="inv-tab-dispatch-reqs">
            Dispatch Requests
            {docReqs.filter((r) => r.status === "Pending").length > 0 && (
              <span className="ml-1.5 inline-flex items-center justify-center bg-rose-600 text-white rounded-full text-[9px] font-bold w-4 h-4">{docReqs.filter((r) => r.status === "Pending").length}</span>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="mt-4">
          {viewMode === "card" ? (
            pendingOrders.length === 0 ? (
              <div className="pk-card p-12 text-center text-slate-400">All orders have invoices generated.</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="inv-pending-cards">
                {pendingOrders.map((o) => {
                  const orderType = o.order_type || "Product";
                  const issued = invoiceCountByOrder[o.id] || 0;
                  const isService = orderType === "Service";
                  const multi = !!o.allow_multiple_invoices;
                  const canIssueMore = isService || multi;
                  return (
                    <div key={o.id} className="pk-card p-4 hover:shadow-md transition-shadow" data-testid={`inv-pending-card-${o.order_no}`}>
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="min-w-0 flex-1">
                          <div className="mono text-[11px] font-bold text-slate-700 truncate flex items-center gap-1.5">{o.order_no} <OrderTypeTag type={o.order_type} testid={`inv-type-card-${o.order_no}`} /></div>
                          <div className="font-bold text-slate-900 truncate mt-0.5">{o.client_name}</div>
                        </div>
                        <div className="flex flex-col items-end gap-1 shrink-0">
                          <Badge className={(isService ? "bg-purple-100 text-purple-700" : "bg-sky-100 text-sky-700") + " text-[10px]"}>{orderType}</Badge>
                          {multi && <Badge className="bg-amber-100 text-amber-800 text-[9.5px]">Multi</Badge>}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-[11.5px] mb-2">
                        <div><div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">Value</div><div className="mono font-semibold text-slate-900">₹ {o.total_value}</div></div>
                        <div><div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">Invoices</div><div>{issued > 0 ? <Badge variant="secondary" className="text-[10px]">{issued} issued</Badge> : <span className="text-slate-400">—</span>}</div></div>
                        <div className="col-span-2"><div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">Created By</div><div className="text-slate-800 text-[11px] truncate">{o.created_by_name}</div></div>
                      </div>
                      <div className="pt-2 border-t border-slate-100">
                        <Button size="sm" onClick={() => openCreate(o)} className="h-7 text-[10.5px] w-full bg-[var(--pk-accent)] hover:bg-[var(--pk-accent-hover)] text-white" data-testid={`inv-generate-card-${o.order_no}`}>
                          <Receipt size={11} className="mr-1" /> {canIssueMore && issued > 0 ? "+ Another Invoice" : "Generate Invoice"}
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )
          ) : (
          <div className="pk-card overflow-x-auto">
            <table className="w-full text-sm responsive-table">
              <thead className="border-b border-slate-200 bg-slate-50/60">
                <tr><Th>Order No</Th><Th>Type</Th><Th>Client</Th><Th>Value</Th><Th>Invoices</Th><Th>Created By</Th><Th className="text-right">Action</Th></tr>
              </thead>
              <tbody>
                {pendingOrders.length === 0 && <tr><td colSpan={7} className="text-center py-12 text-slate-400">All orders have invoices generated.</td></tr>}
                {pendingOrders.map((o) => {
                  const orderType = o.order_type || "Product";
                  const issued = invoiceCountByOrder[o.id] || 0;
                  const isService = orderType === "Service";
                  const multi = !!o.allow_multiple_invoices;
                  const canIssueMore = isService || multi;
                  return (
                    <tr key={o.id} className="border-b border-slate-100" data-testid={`inv-pending-${o.order_no}`}>
                      <td className="px-4 py-3 mono font-semibold" data-label="Order No">{o.order_no}</td>
                      <td className="px-4 py-3" data-label="Type">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <Badge className={isService ? "bg-pink-100 text-pink-700 hover:bg-pink-100 text-[10px]" : "bg-sky-100 text-sky-700 hover:bg-sky-100 text-[10px]"}>{orderType}</Badge>
                          {multi && <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 text-[10px]" data-testid={`inv-multi-flag-${o.order_no}`}>Multi-Invoice</Badge>}
                        </div>
                      </td>
                      <td className="px-4 py-3" data-label="Client">{o.client_name}</td>
                      <td className="px-4 py-3 mono" data-label="Value">₹ {o.total_value}</td>
                      <td className="px-4 py-3 mono text-[12px]" data-label="Invoices">
                        {issued > 0 ? <Badge variant="secondary" className="text-[10px]">{issued} issued</Badge> : <span className="text-slate-400">—</span>}
                      </td>
                      <td className="px-4 py-3 text-[11px]" data-label="Created By">{o.created_by_name}</td>
                      <td className="px-4 py-3 text-right" data-label="">
                        <Button size="sm" onClick={() => openCreate(o)} className="h-8 text-xs bg-[var(--pk-accent)] hover:bg-[var(--pk-accent-hover)] text-white" data-testid={`inv-generate-${o.order_no}`}>
                          <Receipt size={12} className="mr-1" /> {canIssueMore && issued > 0 ? "+ Another Invoice" : "Generate Invoice"}
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          )}
        </TabsContent>

        <TabsContent value="service-calcs" className="mt-4">
          <div className="pk-card p-4 mb-3 bg-violet-50/40 border border-violet-200 flex items-center justify-between flex-wrap gap-2">
            <div className="text-[12px] text-violet-900">
              <strong>{selectedCalcs.size}</strong> selected · Total ₹{" "}
              <span className="mono font-bold">
                {serviceCalcs.filter((c) => selectedCalcs.has(c.id)).reduce((a, c) => a + Number(c.invoice_total || 0), 0).toLocaleString("en-IN")}
              </span>
              <span className="text-violet-700 ml-2 text-[11px]">(Select one or more months of the same Service Order to generate a combined invoice)</span>
            </div>
            <Button
              size="sm"
              disabled={selectedCalcs.size === 0}
              onClick={openCreateFromCalcs}
              data-testid="inv-gen-from-calcs"
            >
              Generate Invoice from Selected
            </Button>
          </div>
          <div className="pk-card overflow-x-auto">
            <table className="w-full text-sm responsive-table" data-testid="inv-service-calcs-table">
              <thead className="border-b border-slate-200 bg-slate-50/60">
                <tr>
                  <Th className="w-10"></Th>
                  <Th>Month</Th>
                  <Th>Order No</Th>
                  <Th>Client</Th>
                  <Th>Service</Th>
                  <Th>Sessions</Th>
                  <Th>Amount</Th>
                  <Th>Generated By</Th>
                </tr>
              </thead>
              <tbody>
                {serviceCalcs.length === 0 && (
                  <tr><td colSpan={8} className="text-center py-12 text-slate-400" data-testid="inv-service-calcs-empty">
                    No pending Service Order calculations. They will appear here once SOM users save a monthly calculation.
                  </td></tr>
                )}
                {serviceCalcs.map((c) => (
                  <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`inv-calc-row-${c.id}`}>
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedCalcs.has(c.id)}
                        onChange={() => toggleCalcSelect(c.id, c.order_id)}
                        className="accent-violet-600"
                        data-testid={`inv-calc-cb-${c.id}`}
                      />
                    </td>
                    <td data-label="Month" className="px-4 py-3 mono font-semibold">{c.month_key}</td>
                    <td data-label="Order No" className="px-4 py-3 mono text-[12px]">{c.order_no || "—"}</td>
                    <td data-label="Client" className="px-4 py-3 font-medium">{c.client_name || "—"}</td>
                    <td data-label="Service" className="px-4 py-3 text-[12px] text-slate-600">{c.product_service || "—"}</td>
                    <td data-label="Sessions" className="px-4 py-3 mono">{c.total_sessions || 0}</td>
                    <td data-label="Amount" className="px-4 py-3 mono font-semibold">₹ {Number(c.invoice_total || 0).toLocaleString("en-IN")}</td>
                    <td data-label="Generated By" className="px-4 py-3 text-[11px] text-slate-500">
                      {c.created_by_name || "—"}
                      {c.is_direct && <Badge className="ml-1 bg-violet-100 text-violet-800 text-[9px]">Direct</Badge>}
                      <div>
                        {!c.is_direct && (
                          <button
                            onClick={async () => {
                              try {
                                const r = await api.get(`/service-orders/${c.order_id}/calculations/${c.id}/invoice-pdf`, { responseType: "blob" });
                                const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
                                const a = document.createElement("a");
                                a.href = url; a.download = `Calc_${c.month_key}.pdf`;
                                document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
                              } catch (e) { toast.error(formatError(e.response?.data?.detail) || "Failed"); }
                            }}
                            className="text-[10px] text-violet-600 underline mt-0.5"
                            data-testid={`inv-calc-pdf-${c.id}`}
                          >
                            Download Calc PDF
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="all" className="mt-4">
          {/* Filter chips for All Invoices */}
          <div className="flex flex-wrap items-center gap-1.5 mb-3" data-testid="inv-filter-chips">
            <span className="text-[10px] uppercase font-bold text-slate-500 mr-1">Payment</span>
            {[
              { v: "Pending", tone: "bg-amber-500" },
              { v: "Paid", tone: "bg-emerald-600" },
              { v: "Overdue", tone: "bg-rose-600" },
              { v: "Cancelled", tone: "bg-slate-500" },
            ].map(({ v, tone }) => (
              <button
                key={v}
                onClick={() => setInvFilter(invFilter === v ? "" : v)}
                className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-[11px] font-semibold transition-all ${invFilter === v ? `${tone} text-white border-transparent shadow-sm` : "bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200 border-slate-200 dark:border-slate-700 hover:border-slate-400"}`}
                data-testid={`inv-chip-${v.toLowerCase()}`}
              >
                {v}
              </button>
            ))}
            {invFilter && (
              <button
                onClick={() => setInvFilter("")}
                className="ml-2 inline-flex items-center gap-1 rounded-full bg-rose-50 border border-rose-200 text-rose-700 px-3 py-1 text-[11px] font-bold hover:bg-rose-100"
                data-testid="inv-chip-clear"
              >
                <X size={11} /> Clear
              </button>
            )}
          </div>
          <div className="relative max-w-md mb-4">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search invoice, client…" className="pl-9 h-10" data-testid="inv-search" />
          </div>
          <div className={viewMode === "card" ? "hidden" : "pk-card overflow-x-auto"}>
            <table className="w-full text-sm responsive-table">
              <thead className="border-b border-slate-200 bg-slate-50/60">
                <tr><Th>Invoice No</Th><Th>Order No</Th><Th>Client</Th><Th>Date</Th><Th>Amount</Th><Th>Due</Th><Th>Status</Th><Th className="text-right">Actions</Th></tr>
              </thead>
              <tbody>
                {filteredInvoices.length === 0 && <tr><td colSpan={8} className="text-center py-12 text-slate-400">No invoices yet.</td></tr>}
                {filteredInvoices.map((i) => {
                  const cancelled = !!i.is_cancelled;
                  const parentOrder = ordersById[i.order_id];
                  const delivered = parentOrder?.status === "Complete Delivered";
                  const hasCreditNote = !!i.credit_note_id;
                  // Edit/Cancel/Delete are blocked once the order is Complete
                  // Delivered. From that point only "Generate Credit Note"
                  // remains. After the CN is issued, even that action hides
                  // and the badge is shown.
                  const lockedByDelivery = delivered && !cancelled;
                  return (
                  <tr key={i.id} className={`border-b border-slate-100 ${cancelled ? "opacity-60" : ""} ${i.is_seriously_overdue ? "bg-rose-50/40" : ""}`} data-testid={`inv-row-${i.invoice_no}`} data-highlight-id={i.id}>
                    <td className="px-4 py-3 mono font-semibold" data-label="Invoice No">
                      {cancelled ? <span className="line-through">{i.invoice_no}</span> : i.invoice_no}
                      {cancelled && <Badge className="bg-red-100 text-red-700 hover:bg-red-100 text-[9px] ml-2" data-testid={`inv-cancelled-${i.invoice_no}`}>Cancelled</Badge>}
                      {hasCreditNote && (
                        <Badge
                          onClick={() => setCnDetail(creditNotes.find((cn) => cn.id === i.credit_note_id) || { credit_note_no: i.credit_note_no, credit_note_amount: i.credit_note_amount, invoice_id: i.id, invoice_no: i.invoice_no, order_no: i.order_no, client_name: i.client_name })}
                          className="bg-emerald-100 text-emerald-800 hover:bg-emerald-200 text-[9px] ml-2 cursor-pointer"
                          data-testid={`inv-cn-badge-${i.invoice_no}`}
                          title={`Credit Note issued for ₹${i.credit_note_amount || 0} — click to view`}
                        >
                          CN · {i.credit_note_no} · View Details
                        </Badge>
                      )}
                      {lockedByDelivery && !hasCreditNote && (
                        <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100 text-[9px] ml-2" data-testid={`inv-delivered-${i.invoice_no}`} title="Order Complete Delivered — only Credit Note action allowed">
                          🔒 Delivered
                        </Badge>
                      )}
                      {i.is_seriously_overdue && !cancelled && (
                        <Badge className="bg-rose-600 text-white hover:bg-rose-700 text-[9px] ml-2 animate-pulse" data-testid={`inv-overdue90-${i.invoice_no}`}>
                          ORDER DUE · {i.days_overdue}d
                        </Badge>
                      )}
                    </td>
                    <td className="px-4 py-3 mono text-[12px] text-slate-600" data-label="Order No"><span className="inline-flex items-center gap-1.5">{i.order_no} <OrderTypeTag type={i.order_type} testid={`inv-type-${i.id}`} /></span></td>
                    <td className="px-4 py-3" data-label="Client">{i.client_name}</td>
                    <td className="px-4 py-3 mono text-[12px]" data-label="Date">{i.invoice_date}</td>
                    <td className="px-4 py-3 mono" data-label="Amount">₹ {i.amount}</td>
                    <td className="px-4 py-3" data-label="Due">
                      <Input type="date" defaultValue={i.due_date || ""} onBlur={(e) => { if (e.target.value !== i.due_date) updateDueDate(i, e.target.value); }} className="h-8 w-36 text-xs mono" data-testid={`inv-due-${i.invoice_no}`} disabled={i.is_paid || cancelled} />
                    </td>
                    <td className="px-4 py-3" data-label="Status"><Badge className={PAY_COLOR[cancelled ? "Cancelled" : i.payment_status] || "bg-slate-100"}>{cancelled ? "Cancelled" : i.payment_status}</Badge></td>
                    <td className="px-4 py-3 text-right" data-label="Actions">
                      <div className="flex items-center justify-end gap-2">
                        <Button size="sm" variant="outline" onClick={() => viewOrderDetails(i.order_id)} className="h-8 text-xs text-sky-700 border-sky-200 hover:bg-sky-50" data-testid={`inv-view-order-${i.invoice_no}`} title={i.order_type === "Service" ? "View order details" : "View order & tracking details"}>
                          <Eye size={12} className="mr-1" /> View
                        </Button>
                        {!cancelled && <Switch checked={!!i.is_paid} onCheckedChange={() => togglePaid(i)} data-testid={`inv-paid-${i.invoice_no}`} />}
                        {!cancelled && !lockedByDelivery && <Button size="sm" variant="outline" onClick={() => openEditInvoice(i)} className="h-8 text-xs" data-testid={`inv-edit-${i.invoice_no}`}>Edit</Button>}
                        {(i.source_calculation_ids?.length > 0) && (
                          <Button size="sm" variant="outline" onClick={() => downloadAnnexure(i)} className="h-8 text-xs text-violet-700 border-violet-200 hover:bg-violet-50" data-testid={`inv-annexure-${i.invoice_no}`} title="Session Log Annexure">
                            Annexure
                          </Button>
                        )}
                        {!cancelled && !lockedByDelivery && (
                          <Button size="sm" variant="outline" onClick={() => cancelInvoice(i)} className="h-8 text-xs text-amber-700 border-amber-200 hover:bg-amber-50" data-testid={`inv-cancel-${i.invoice_no}`}>
                            Mark Cancelled
                          </Button>
                        )}
                        {lockedByDelivery && !hasCreditNote && (
                          <Button size="sm" onClick={() => openCreditNote(i)} className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`inv-cn-${i.invoice_no}`}>
                            <Receipt size={11} className="mr-1" /> Generate Credit Note
                          </Button>
                        )}
                        {hasCreditNote && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setCnDetail(creditNotes.find((cn) => cn.id === i.credit_note_id) || { credit_note_no: i.credit_note_no, credit_note_amount: i.credit_note_amount, invoice_id: i.id, invoice_no: i.invoice_no, order_no: i.order_no, client_name: i.client_name })}
                            className="h-8 text-xs bg-emerald-50 border-emerald-300 text-emerald-800 hover:bg-emerald-100"
                            data-testid={`inv-cn-view-${i.invoice_no}`}
                          >
                            <Receipt size={11} className="mr-1" /> Credit Note Generated · View Details
                          </Button>
                        )}
                        {isMaster && lockedByDelivery && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => reopenOrder(i.order_id, "Pending Dispatch", true)}
                            className="h-8 text-xs text-amber-700 border-amber-300 hover:bg-amber-50"
                            data-testid={`inv-reopen-${i.invoice_no}`}
                            title="Rewind the order so other teams can re-edit it"
                          >
                            ↺ Reopen Order
                          </Button>
                        )}
                        {isMaster && !lockedByDelivery && !hasCreditNote && (
                          <Button size="sm" variant="outline" onClick={() => deleteInvoice(i)} className="h-8 text-xs text-red-600 border-red-200 hover:bg-red-50" data-testid={`inv-delete-${i.invoice_no}`}>
                            <X size={12} />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {viewMode === "card" && (
            filteredInvoices.length === 0 ? (
              <div className="pk-card p-12 text-center text-slate-400">No invoices yet.</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="inv-cards">
                {filteredInvoices.map((i) => {
                  const cancelled = !!i.is_cancelled;
                  const parentOrder = ordersById[i.order_id];
                  const delivered = parentOrder?.status === "Complete Delivered";
                  const hasCreditNote = !!i.credit_note_id;
                  const lockedByDelivery = delivered && !cancelled;
                  return (
                    <div key={i.id} className={`pk-card p-4 hover:shadow-md transition-shadow ${cancelled ? "opacity-60" : ""} ${i.is_seriously_overdue ? "border-rose-300 bg-rose-50/30" : ""}`} data-testid={`inv-card-${i.invoice_no}`} data-highlight-id={i.id}>
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="min-w-0 flex-1">
                          <div className="mono text-[11.5px] font-bold text-slate-700 truncate">{cancelled ? <span className="line-through">{i.invoice_no}</span> : i.invoice_no}</div>
                          <div className="font-bold text-slate-900 truncate mt-0.5">{i.client_name}</div>
                          <div className="mono text-[10.5px] text-slate-500 truncate flex items-center gap-1.5">Order {i.order_no} <OrderTypeTag type={i.order_type} testid={`inv-type-card2-${i.id}`} /></div>
                        </div>
                        <Badge className={(PAY_COLOR[cancelled ? "Cancelled" : i.payment_status] || "bg-slate-100") + " text-[10px] shrink-0"}>{cancelled ? "Cancelled" : i.payment_status}</Badge>
                      </div>
                      <div className="flex flex-wrap gap-1 mb-2">
                        {hasCreditNote && <Badge className="bg-emerald-100 text-emerald-800 text-[9.5px]">CN · {i.credit_note_no}</Badge>}
                        {lockedByDelivery && !hasCreditNote && <Badge className="bg-blue-100 text-blue-800 text-[9.5px]">🔒 Delivered</Badge>}
                        {i.is_seriously_overdue && !cancelled && <Badge className="bg-rose-600 text-white text-[9.5px]">ORDER DUE · {i.days_overdue}d</Badge>}
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-[11.5px] mb-2">
                        <div><div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">Date</div><div className="mono text-slate-800">{i.invoice_date}</div></div>
                        <div><div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">Amount</div><div className="mono font-semibold text-slate-900">₹ {i.amount}</div></div>
                        <div className="col-span-2"><div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">Due Date</div><Input type="date" defaultValue={i.due_date || ""} onBlur={(e) => { if (e.target.value !== i.due_date) updateDueDate(i, e.target.value); }} className="h-7 w-full text-[11px] mono" data-testid={`inv-due-card-${i.invoice_no}`} disabled={i.is_paid || cancelled} /></div>
                      </div>
                      <div className="pt-2 border-t border-slate-100 flex flex-wrap gap-1.5 items-center">
                        <Button size="sm" variant="outline" onClick={() => viewOrderDetails(i.order_id)} className="h-7 px-2 text-[10.5px] text-sky-700 border-sky-200" data-testid={`inv-view-order-card-${i.invoice_no}`}><Eye size={10} className="mr-1" /> View</Button>
                        {!cancelled && <div className="flex items-center gap-1 text-[10px]"><Switch checked={!!i.is_paid} onCheckedChange={() => togglePaid(i)} data-testid={`inv-paid-card-${i.invoice_no}`} /><span>{i.is_paid ? "Paid" : "Unpaid"}</span></div>}
                        {!cancelled && !lockedByDelivery && <Button size="sm" variant="outline" onClick={() => openEditInvoice(i)} className="h-7 px-2 text-[10.5px]" data-testid={`inv-edit-card-${i.invoice_no}`}>Edit</Button>}
                        {lockedByDelivery && !hasCreditNote && <Button size="sm" onClick={() => openCreditNote(i)} className="h-7 px-2 text-[10.5px] bg-emerald-600 text-white" data-testid={`inv-cn-card-${i.invoice_no}`}><Receipt size={10} className="mr-1" /> CN</Button>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )
          )}
        </TabsContent>

        <TabsContent value="credit-notes" className="mt-4">
          <div className="pk-card overflow-x-auto" data-testid="inv-credit-notes-tab">
            <table className="w-full text-sm responsive-table">
              <thead className="border-b border-slate-200 bg-slate-50/60">
                <tr>
                  <th className="px-4 py-3 text-left pk-table-header">CN No</th>
                  <th className="px-4 py-3 text-left pk-table-header">Date</th>
                  <th className="px-4 py-3 text-left pk-table-header">Invoice</th>
                  <th className="px-4 py-3 text-left pk-table-header">Order</th>
                  <th className="px-4 py-3 text-left pk-table-header">Client</th>
                  <th className="px-4 py-3 text-left pk-table-header">Amount</th>
                  <th className="px-4 py-3 text-left pk-table-header">Issued By</th>
                  <th className="px-4 py-3 text-right pk-table-header">Action</th>
                </tr>
              </thead>
              <tbody>
                {creditNotes.length === 0 && <tr><td colSpan={8} className="text-center py-12 text-slate-400 text-sm">No credit notes issued yet.</td></tr>}
                {creditNotes.map((cn) => (
                  <tr key={cn.id} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`inv-cn-row-${cn.credit_note_no}`}>
                    <td className="px-4 py-3 mono font-semibold text-emerald-700" data-label="CN No">{cn.credit_note_no}</td>
                    <td className="px-4 py-3 mono text-[12px]" data-label="Date">{cn.credit_note_date}</td>
                    <td className="px-4 py-3 mono text-[12px]" data-label="Invoice">{cn.invoice_no}</td>
                    <td className="px-4 py-3 mono text-[12px]" data-label="Order">{cn.order_no}</td>
                    <td className="px-4 py-3" data-label="Client">{cn.client_name}</td>
                    <td className="px-4 py-3 mono font-semibold" data-label="Amount">₹ {Number(cn.amount || 0).toLocaleString("en-IN")}</td>
                    <td className="px-4 py-3 text-[11.5px] text-slate-600" data-label="Issued By">{cn.created_by_name}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap" data-label="Action">
                      <Button size="sm" variant="outline" onClick={() => setCnDetail(cn)} className="h-8 text-xs mr-1.5" data-testid={`inv-cn-detail-${cn.credit_note_no}`}>
                        View Details
                      </Button>
                      {(isMaster || moduleCanDo(user, "sales_orders", "edit")) && (
                        <>
                          <Button size="sm" variant="outline" onClick={() => openEditCN(cn)} className="h-8 text-xs mr-1.5" data-testid={`inv-cn-edit-${cn.credit_note_no}`}>
                            Edit
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => deleteCreditNote(cn)} className="h-8 text-xs text-rose-700 border-rose-200 hover:bg-rose-50" data-testid={`inv-cn-delete-${cn.credit_note_no}`}>
                            Delete
                          </Button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
        <TabsContent value="dispatch-reqs" className="mt-4">
          <div className="pk-card p-3 mb-4 bg-amber-50/60 border-amber-200 text-[12px] text-slate-700">
            Dispatch admin requests for <strong>Bill Copy</strong> or <strong>Quote</strong> URLs land here. Click <em>Share URL</em> on any Pending row to paste the document link — the requester is notified instantly.
          </div>
          <div className="pk-card overflow-x-auto">
            <table className="w-full text-sm responsive-table">
              <thead className="border-b border-slate-200 bg-slate-50/60">
                <tr>
                  <th className="px-4 py-3 text-left pk-table-header">Order</th>
                  <th className="px-4 py-3 text-left pk-table-header">Client</th>
                  <th className="px-4 py-3 text-left pk-table-header">Type</th>
                  <th className="px-4 py-3 text-left pk-table-header">Status</th>
                  <th className="px-4 py-3 text-left pk-table-header">URL</th>
                  <th className="px-4 py-3 text-left pk-table-header">Requested By</th>
                  <th className="px-4 py-3 text-right pk-table-header">Action</th>
                </tr>
              </thead>
              <tbody>
                {docReqs.length === 0 && <tr><td colSpan={7} className="text-center py-12 text-slate-400 text-sm">No dispatch document requests yet.</td></tr>}
                {docReqs.map((r) => (
                  <tr key={r.id} className="border-b border-slate-100" data-testid={`inv-doc-req-${r.id}`}>
                    <td className="px-4 py-3 mono font-semibold" data-label="Order">{r.order_no}</td>
                    <td className="px-4 py-3" data-label="Client">{r.client_name}</td>
                    <td className="px-4 py-3" data-label="Type">
                      <Badge className="bg-violet-100 text-violet-700 hover:bg-violet-100 text-[10px]">
                        {r.request_type === "bill_copy" ? "Bill Copy" : "Quote"}
                      </Badge>
                    </td>
                    <td className="px-4 py-3" data-label="Status">
                      <Badge className={r.status === "Fulfilled" ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-[10px]" : "bg-amber-100 text-amber-800 hover:bg-amber-100 text-[10px]"}>
                        {r.status}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-[12px]" data-label="URL">
                      {r.url ? (
                        <a href={r.url} target="_blank" rel="noreferrer" className="text-[var(--pk-accent)] underline mono truncate inline-block max-w-[260px]" data-testid={`inv-doc-url-${r.id}`}>
                          {r.url}
                        </a>
                      ) : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-4 py-3 text-[12px] text-slate-600" data-label="Requested By">
                      {r.requested_by_name}
                      <div className="text-[10px] text-slate-400">{r.requested_at ? new Date(r.requested_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" }) : ""}</div>
                    </td>
                    <td className="px-4 py-3 text-right" data-label="Action">
                      {r.status === "Pending" ? (
                        <Button onClick={() => fulfillDocReq(r)} size="sm" className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`inv-doc-share-${r.id}`}>
                          Share URL
                        </Button>
                      ) : (
                        <span className="text-[11px] text-slate-400">Shared</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      {/* Generate Invoice Dialog */}
      <Dialog open={!!creating} onOpenChange={(o) => !o && setCreating(null)}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="heading">Generate Invoice — {creating?.order_no}</DialogTitle>
            <div className="text-[11px] text-slate-500 mt-1">
              {orderEditable ? (
                <span>Review the order details below. If anything needs to change before invoicing, click <span className="font-semibold text-emerald-700">Edit</span>. Changes follow the same rules as Order Creation and are recorded in the Order Change Log. The order locks once this invoice is generated.</span>
              ) : (
                <span>Order details are <span className="font-semibold text-amber-700">read-only</span> — either the order is already locked by an existing invoice or you don&apos;t have edit rights.</span>
              )}
            </div>
          </DialogHeader>
          {/* Order Details — shown as a read-only summary first. The user
              clicks "Edit" to flip into the full editable Order Form. The
              edit mode is only available before any invoice exists for this
              order and only to Master / Accounts / Order Creator. */}
          {creating && (
            <div className="border border-slate-200 dark:border-slate-700 rounded-md p-3 bg-slate-50/40 dark:bg-slate-800/30 mb-3" data-testid="inv-embedded-order-form">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[11px] uppercase tracking-wider font-semibold text-violet-700 dark:text-violet-300">
                  Order Details {orderEditMode ? "(editing)" : ""}
                </div>
                {!orderEditMode && orderEditable && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setOrderEditMode(true)}
                    className="h-7 text-[11px]"
                    data-testid="inv-order-edit-btn"
                  >
                    Edit
                  </Button>
                )}
                {orderEditMode && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      // Discard edits — reset form back to the snapshot.
                      if (orderOriginal) setOrderForm(orderOriginal);
                      setOrderEditRemark("");
                      setOrderEditMode(false);
                    }}
                    className="h-7 text-[11px] text-slate-500"
                    data-testid="inv-order-edit-cancel"
                  >
                    Discard edits
                  </Button>
                )}
              </div>
              {orderEditMode ? (
                <>
                  <OrderForm form={orderForm} setForm={setOrderForm} canOverride={isMaster || moduleCanDo(user, "sales_orders", "edit")} />
                  {orderChangedFields().length > 0 && (
                    <div className="border-t border-amber-200 mt-3 pt-3">
                      <Label className="text-[11px] uppercase tracking-wider font-semibold text-amber-700">
                        Reason for change * <span className="text-[10px] normal-case font-normal text-slate-500">({orderChangedFields().length} field{orderChangedFields().length === 1 ? "" : "s"} changed: {orderChangedFields().slice(0, 5).join(", ")}{orderChangedFields().length > 5 ? "…" : ""})</span>
                      </Label>
                      <Textarea
                        rows={2}
                        value={orderEditRemark}
                        onChange={(e) => setOrderEditRemark(e.target.value)}
                        placeholder="Describe what was changed and why (audit trail)"
                        className="mt-1.5"
                        data-testid="inv-order-edit-remark"
                      />
                    </div>
                  )}
                </>
              ) : (
                <OrderSummaryCard order={creating} />
              )}
              {!orderEditable && !orderEditMode && (
                <div className="text-[10.5px] italic text-amber-700 dark:text-amber-300 mt-2">
                  Read-only — either you don&apos;t have edit rights or this order is locked by an existing invoice.
                </div>
              )}
            </div>
          )}
          <div className="text-[11px] uppercase tracking-wider font-semibold text-violet-700 dark:text-violet-300 mt-1 mb-1">
            Invoice Details
          </div>
          <div className="grid grid-cols-2 gap-3 py-2">
            <F label="Invoice No. *"><Input value={form.invoice_no} onChange={(e) => setForm({ ...form, invoice_no: e.target.value })} data-testid="inv-form-no" /></F>
            <F label="Invoice Date *"><Input type="date" value={form.invoice_date} onChange={(e) => setForm({ ...form, invoice_date: e.target.value })} data-testid="inv-form-date" /></F>
            <GstAmountFields form={form} setForm={setForm} idPrefix="inv-form" />
            {creating?.is_gem_contract && (
              <>
                <F label="GeM Invoice No."><Input value={form.gem_invoice_no} onChange={(e) => setForm({ ...form, gem_invoice_no: e.target.value })} data-testid="inv-form-gem-no" /></F>
                <F label="GeM Invoice URL" full><Input value={form.gem_invoice_url} onChange={(e) => setForm({ ...form, gem_invoice_url: e.target.value })} placeholder="Paste uploaded link" data-testid="inv-form-gem-url" /></F>
              </>
            )}
            <F label="Remark" full><Textarea rows={2} value={form.remark} onChange={(e) => setForm({ ...form, remark: e.target.value })} data-testid="inv-form-remark" /></F>

            <F label="E-way Number"><Input value={form.eway_no} onChange={(e) => setForm({ ...form, eway_no: e.target.value })} placeholder="12-digit e-way bill no." className="mono" data-testid="inv-form-eway-no" /></F>
            <F label="Dispatch Via"><Input value={form.dispatch_via} onChange={(e) => setForm({ ...form, dispatch_via: e.target.value })} placeholder="e.g. Bluedart, Self pickup, DTDC" data-testid="inv-form-dispatch-via" /></F>

            <div className="md:col-span-2 flex items-center gap-3 px-1 py-2 border-t border-slate-100 pt-3 mt-1">
              <Switch checked={form.is_paid} onCheckedChange={(v) => setForm({ ...form, is_paid: v })} data-testid="inv-form-paid" />
              <span className="text-sm text-slate-700">Amount already paid?</span>
            </div>
            {!form.is_paid && (
              <F label="Due Date *" full>
                <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="inv-form-due" />
              </F>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreating(null)}>Cancel</Button>
            <Button onClick={submit} disabled={submitting} className="bg-[var(--pk-accent)] hover:bg-[var(--pk-accent-hover)] text-white" data-testid="inv-form-submit">
              {submitting ? "Generating…" : "Generate Invoice"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Edit Invoice Dialog (Master Admin or Accounts Admin, mandatory remark) */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="heading">Edit Invoice — {editing?.invoice_no}</DialogTitle>
            <div className="text-[11px] text-slate-500">Editing as <span className="font-semibold">{isMaster ? "Master Admin" : "Accounts Admin"}</span>. A remark is required for the audit trail.</div>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-2">
            <F label="Invoice No."><Input value={form.invoice_no} onChange={(e) => setForm({ ...form, invoice_no: e.target.value })} data-testid="inv-edit-no" /></F>
            <F label="Invoice Date"><Input type="date" value={form.invoice_date} onChange={(e) => setForm({ ...form, invoice_date: e.target.value })} data-testid="inv-edit-date" /></F>
            <GstAmountFields form={form} setForm={setForm} idPrefix="inv-edit" />
            <F label="Due Date"><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="inv-edit-due" /></F>
            <F label="GeM Invoice No."><Input value={form.gem_invoice_no} onChange={(e) => setForm({ ...form, gem_invoice_no: e.target.value })} data-testid="inv-edit-gem-no" /></F>
            <F label="GeM Invoice URL"><Input value={form.gem_invoice_url} onChange={(e) => setForm({ ...form, gem_invoice_url: e.target.value })} data-testid="inv-edit-gem-url" /></F>
            <F label="E-way Number"><Input value={form.eway_no} onChange={(e) => setForm({ ...form, eway_no: e.target.value })} placeholder="12-digit e-way bill no." className="mono" data-testid="inv-edit-eway-no" /></F>
            <F label="Dispatch Via"><Input value={form.dispatch_via} onChange={(e) => setForm({ ...form, dispatch_via: e.target.value })} placeholder="e.g. Bluedart, Self pickup, DTDC" data-testid="inv-edit-dispatch-via" /></F>
            <F label="Remark" full><Textarea rows={2} value={form.remark} onChange={(e) => setForm({ ...form, remark: e.target.value })} data-testid="inv-edit-remark" /></F>
            <div className="md:col-span-2 flex items-center gap-3 px-1 py-1 border-t border-slate-100 pt-3">
              <Switch checked={form.is_paid} onCheckedChange={(v) => setForm({ ...form, is_paid: v })} data-testid="inv-edit-paid" />
              <span className="text-sm text-slate-700">Paid?</span>
            </div>
            <div className="md:col-span-2 border-t border-amber-100 pt-3 mt-1">
              <Label className="text-[11px] uppercase tracking-wider font-semibold text-amber-700">Reason for change *</Label>
              <Textarea rows={2} value={form.edit_remark || ""} onChange={(e) => setForm({ ...form, edit_remark: e.target.value })} placeholder="Describe what was changed and why (audit trail)" className="mt-1.5" data-testid="inv-edit-reason" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={saveEditInvoice} className="bg-[var(--pk-accent)] hover:bg-[var(--pk-accent-hover)] text-white" data-testid="inv-edit-save">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Credit Note Dialog */}
      <Dialog open={!!cnTarget} onOpenChange={(o) => !o && setCnTarget(null)}>
        <DialogContent className="max-w-md" data-testid="inv-cn-dialog">
          <DialogHeader>
            <DialogTitle className="heading">Generate Credit Note — {cnTarget?.invoice_no}</DialogTitle>
            <div className="text-[11px] text-slate-500">
              Order <span className="font-semibold">{cnTarget?.order_no}</span> ·
              Client <span className="font-semibold">{cnTarget?.client_name}</span> ·
              Invoice Amount ₹ {cnTarget?.amount}
            </div>
            <div className="text-[10.5px] text-slate-500 mt-1 italic">
              Once issued, the invoice locks and Dispatch can fill recall tracking against this order.
            </div>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-2">
            <F label="Credit Note No. *" full>
              <Input
                value={cnForm.credit_note_no}
                onChange={(e) => setCnForm({ ...cnForm, credit_note_no: e.target.value })}
                placeholder="e.g. CN/2026/PV-0001"
                className="mono"
                data-testid="inv-cn-no"
              />
              <div className="text-[10.5px] text-slate-500 mt-1">Leave blank to auto-generate (CN-YYYY-NNNN).</div>
            </F>
            <F label="Credit Note Date *">
              <Input type="date" value={cnForm.credit_note_date} onChange={(e) => setCnForm({ ...cnForm, credit_note_date: e.target.value })} data-testid="inv-cn-date" />
            </F>
            <F label="Amount (INR) *">
              <Input value={cnForm.amount} onChange={(e) => setCnForm({ ...cnForm, amount: e.target.value })} placeholder="e.g. 5000" data-testid="inv-cn-amount" />
            </F>
            <F label="Reason *" full>
              <Textarea rows={2} value={cnForm.reason} onChange={(e) => setCnForm({ ...cnForm, reason: e.target.value })} placeholder="e.g. Product defective on delivery, customer requested credit" data-testid="inv-cn-reason" />
            </F>
            <F label="Remark" full>
              <Textarea rows={2} value={cnForm.remark} onChange={(e) => setCnForm({ ...cnForm, remark: e.target.value })} placeholder="Optional internal note" data-testid="inv-cn-remark" />
            </F>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCnTarget(null)}>Cancel</Button>
            <Button onClick={submitCreditNote} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="inv-cn-submit">
              <Receipt size={14} className="mr-1.5" /> Issue Credit Note
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Credit Note Detail viewer */}
      <Dialog open={!!cnDetail} onOpenChange={(o) => !o && setCnDetail(null)}>
        <DialogContent className="max-w-lg" data-testid="inv-cn-detail-dialog">
          <DialogHeader>
            <DialogTitle className="heading flex items-center gap-2">
              <Receipt size={16} className="text-emerald-600" /> Credit Note · {cnDetail?.credit_note_no}
            </DialogTitle>
            <div className="text-[11px] text-slate-500">Order {cnDetail?.order_no} · Invoice {cnDetail?.invoice_no} · {cnDetail?.client_name}</div>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-2 text-[12px]">
            <DK label="Amount" v={`₹ ${Number(cnDetail?.amount || 0).toLocaleString("en-IN")}`} />
            <DK label="Date" v={cnDetail?.credit_note_date || "—"} />
            <DK label="Issued By" v={`${cnDetail?.created_by_name || "—"}${cnDetail?.created_by_role ? ` (${cnDetail.created_by_role})` : ""}`} full />
            <DK label="Reason" v={cnDetail?.reason || "—"} full />
            {cnDetail?.remark && <DK label="Internal Remark" v={cnDetail.remark} full />}
            {cnDetail?.created_at && (
              <DK label="Issued At" v={new Date(cnDetail.created_at).toLocaleString("en-IN")} full />
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCnDetail(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Credit Note Edit dialog (Master / Accounts Admin) */}
      <Dialog open={!!cnEditing} onOpenChange={(o) => !o && setCnEditing(null)}>
        <DialogContent className="max-w-lg" data-testid="inv-cn-edit-dialog">
          <DialogHeader>
            <DialogTitle className="heading">Edit Credit Note — {cnEditing?.credit_note_no}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-2">
            <F label="Credit Note No. *" full>
              <Input value={cnEditing?.credit_note_no || ""} onChange={(e) => setCnEditing({ ...cnEditing, credit_note_no: e.target.value })} className="mono" data-testid="inv-cn-edit-no" />
            </F>
            <F label="Date *">
              <Input type="date" value={cnEditing?.credit_note_date || ""} onChange={(e) => setCnEditing({ ...cnEditing, credit_note_date: e.target.value })} data-testid="inv-cn-edit-date" />
            </F>
            <F label="Amount (INR) *">
              <Input value={cnEditing?.amount || ""} onChange={(e) => setCnEditing({ ...cnEditing, amount: e.target.value })} data-testid="inv-cn-edit-amount" />
            </F>
            <F label="Reason *" full>
              <Textarea rows={2} value={cnEditing?.reason || ""} onChange={(e) => setCnEditing({ ...cnEditing, reason: e.target.value })} data-testid="inv-cn-edit-reason" />
            </F>
            <F label="Remark" full>
              <Textarea rows={2} value={cnEditing?.remark || ""} onChange={(e) => setCnEditing({ ...cnEditing, remark: e.target.value })} data-testid="inv-cn-edit-remark" />
            </F>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCnEditing(null)}>Cancel</Button>
            <Button onClick={saveEditCN} className="bg-[var(--pk-accent)] hover:bg-[var(--pk-accent-hover)] text-white" data-testid="inv-cn-edit-save">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Fulfill Dispatch Doc Request Dialog */}
      <Dialog open={!!docFulfillForm} onOpenChange={(o) => { if (!o) setDocFulfillForm(null); }}>
        <DialogContent className="max-w-md">
          {docFulfillForm && (
            <>
              <DialogHeader>
                <DialogTitle className="heading">
                  Share {docFulfillForm.req.request_type === "bill_copy" ? "Bill Copy" : "Quote"} — {docFulfillForm.req.order_no}
                </DialogTitle>
                <div className="text-[11px] text-slate-500">{docFulfillForm.req.client_name}</div>
              </DialogHeader>
              <div className="space-y-3 py-2">
                <F label={`${docFulfillForm.req.request_type === "bill_copy" ? "Bill Copy" : "Quote"} URL *`} full>
                  <Input
                    value={docFulfillForm.url}
                    onChange={(e) => setDocFulfillForm({ ...docFulfillForm, url: e.target.value })}
                    placeholder="Paste uploaded link"
                    data-testid="doc-fulfill-url"
                  />
                </F>
                <div className="grid grid-cols-1 gap-3 pt-2 border-t border-slate-100">
                  <div className="text-[11px] uppercase tracking-wider font-semibold text-violet-700">E-way Info (optional)</div>
                  <F label="E-way URL" full>
                    <Input
                      value={docFulfillForm.eway_url}
                      onChange={(e) => setDocFulfillForm({ ...docFulfillForm, eway_url: e.target.value })}
                      placeholder="E-way bill PDF link"
                      data-testid="doc-fulfill-eway-url"
                    />
                  </F>
                  <F label="E-way Number" full>
                    <Input
                      value={docFulfillForm.eway_no}
                      onChange={(e) => setDocFulfillForm({ ...docFulfillForm, eway_no: e.target.value })}
                      placeholder="12-digit e-way bill no."
                      className="mono"
                      data-testid="doc-fulfill-eway-no"
                    />
                  </F>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDocFulfillForm(null)}>Cancel</Button>
                <Button onClick={submitDocFulfill} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="doc-fulfill-submit">
                  Share with Dispatch
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
      <OrderDetailDialog viewing={viewingOrder} onClose={() => setViewingOrder(null)} currentUserId={user?.id} readOnly />

      <DeleteWithReasonDialog
        open={!!pendingDeleteInvoice}
        onClose={() => setPendingDeleteInvoice(null)}
        title={pendingDeleteInvoice ? `Delete invoice ${pendingDeleteInvoice.invoice_no}?` : "Delete invoice?"}
        description="It will move to Admin Hub → Recycle Bin. Master Admin can restore it later."
        onConfirm={confirmDeleteInvoice}
        testid="invoice-delete"
      />
      <DeleteWithReasonDialog
        open={!!pendingDeleteCN}
        onClose={() => setPendingDeleteCN(null)}
        title={pendingDeleteCN ? `Delete Credit Note ${pendingDeleteCN.credit_note_no}?` : "Delete Credit Note?"}
        description="This will restore the invoice/order to the pre-CN state. The CN moves to Admin Hub → Recycle Bin; Master Admin can restore it later."
        onConfirm={confirmDeleteCN}
        testid="cn-delete"
      />
    </div>
  );
}

function emptyForm() {
  return { order_id: "", invoice_no: "", invoice_date: new Date().toISOString().slice(0, 10), gem_invoice_no: "", gem_invoice_url: "", amount: "", amount_before_gst: "", gst_mode: "percent", gst_percent: "18", gst_amount: "", remark: "", is_paid: false, due_date: "", eway_no: "", dispatch_via: "", source_calculation_ids: [] };
}

function Th({ children, className = "" }) {
  return <th className={`px-4 py-3 text-left pk-table-header ${className}`}>{children}</th>;
}
function F({ label, children, full }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
function DK({ label, v, full }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">{label}</div>
      <div className="text-slate-800 dark:text-slate-100 mt-0.5 whitespace-pre-wrap break-words">{v || "—"}</div>
    </div>
  );
}


// v107 — Compact GST split fields used by both the "Generate Invoice" and
// "Edit Invoice" dialogs. Keeps `amount`, `amount_before_gst`, `gst_percent`
// and `gst_amount` in sync so the Incentive module can auto-fetch the true
// pre-GST invoice value later.
function GstAmountFields({ form, setForm, idPrefix }) {
  const before = parseFloat(form.amount_before_gst) || 0;
  const pct = parseFloat(form.gst_percent) || 0;
  const setBefore = (v) => {
    const b = parseFloat(v) || 0;
    const gstAmt = +(b * pct / 100).toFixed(2);
    const total = +(b + gstAmt).toFixed(2);
    setForm({ ...form, amount_before_gst: v, gst_amount: String(gstAmt), amount: String(total) });
  };
  const setPct = (v) => {
    const p = parseFloat(v) || 0;
    const gstAmt = +(before * p / 100).toFixed(2);
    const total = +(before + gstAmt).toFixed(2);
    setForm({ ...form, gst_percent: v, gst_amount: String(gstAmt), amount: String(total) });
  };
  return (
    <>
      <F label="Before GST Amount (INR) *">
        <Input type="number" value={form.amount_before_gst} onChange={(e) => setBefore(e.target.value)} placeholder="0.00" className="mono" data-testid={`${idPrefix}-before-gst`} />
      </F>
      <F label="GST %">
        <Input type="number" value={form.gst_percent} onChange={(e) => setPct(e.target.value)} placeholder="18" className="mono" data-testid={`${idPrefix}-gst-pct`} />
      </F>
      <F label="GST Amount (auto)">
        <Input value={form.gst_amount || "0"} readOnly className="mono bg-slate-50 dark:bg-slate-900" data-testid={`${idPrefix}-gst-amt`} />
      </F>
      <F label="Total Amount (auto)">
        <Input value={form.amount || "0"} readOnly className="mono font-semibold bg-slate-50 dark:bg-slate-900" data-testid={`${idPrefix}-amount`} />
      </F>
    </>
  );
}


// Compact read-only summary of the order shown in the Generate Invoice
// dialog before the user opts to edit. Mirrors the key fields a creator/
// admin needs to verify against the invoice being raised.
function SummaryRow({ label, value }) {
  return (
    <div className="grid grid-cols-3 gap-2 text-[12px] py-1 border-b border-slate-100 dark:border-slate-700 last:border-b-0">
      <div className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold col-span-1">{label}</div>
      <div className="col-span-2 text-slate-800 dark:text-slate-100 whitespace-pre-wrap break-words">{value || "—"}</div>
    </div>
  );
}

function OrderSummaryCard({ order }) {
  if (!order) return null;
  const products = order.products || [];
  const billing = [order.billing_address, order.billing_district, order.billing_state, order.billing_pincode].filter(Boolean).join(", ");
  return (
    <div data-testid="inv-order-summary">
      <SummaryRow label="Order No." value={order.order_no} />
      <SummaryRow label="Client" value={order.client_name} />
      {order.invoice_company_name && <SummaryRow label="Invoice Company" value={order.invoice_company_name} />}
      <SummaryRow label="Order Type" value={order.order_type || "Product"} />
      <SummaryRow label="Contact" value={order.contact_name ? `${order.contact_name}${order.contact_number ? ` · ${order.contact_number}` : ""}` : ""} />
      <SummaryRow label="Sales Associate" value={order.sale_associate_name} />
      <SummaryRow label="Total Value" value={order.total_value ? `₹ ${order.total_value}` : ""} />
      {(order.commission_value || order.commission) && (
        <SummaryRow
          label="Commission"
          value={
            order.commission_type === "percent" || order.commission_type === "%"
              ? `${order.commission_value || order.commission}%`
              : `₹ ${order.commission_value || order.commission}`
          }
        />
      )}
      <SummaryRow label="Billing Address" value={billing} />
      {order.shipping_address && !order.shipping_same_as_billing && (
        <SummaryRow label="Shipping Address" value={`${order.shipping_address}${order.shipping_pincode ? ` — ${order.shipping_pincode}` : ""}`} />
      )}
      {order.is_gem_contract && (
        <SummaryRow label="GeM Contract" value={`${order.gem_contract_no || ""}${order.gem_contract_date ? ` · ${order.gem_contract_date}` : ""}`} />
      )}
      {order.has_po && <SummaryRow label="PO" value={`${order.po_number || ""}${order.po_date ? ` · ${order.po_date}` : ""}`} />}
      {products.length > 0 && (
        <div className="pt-2 mt-1">
          <div className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Products / Services ({products.length})</div>
          <div className="space-y-1">
            {products.map((p, i) => (
              <div key={i} className="text-[11.5px] text-slate-700 dark:text-slate-200 px-2 py-1 rounded bg-white/60 dark:bg-slate-900/40 border border-slate-100 dark:border-slate-700">
                <span className="font-semibold">{p.product_service || p.product_name || p.name || "—"}</span>
                {p.qty ? <span className="text-slate-500"> · Qty {p.qty}</span> : null}
                {p.amount || p.price ? <span className="text-slate-500"> · ₹ {p.amount || p.price}</span> : null}
              </div>
            ))}
          </div>
        </div>
      )}
      {order.remark && (
        <div className="pt-2 mt-1 text-[11.5px] italic text-slate-600 dark:text-slate-300">“{order.remark}”</div>
      )}
    </div>
  );
}
